import { useContext, useEffect, useState } from "react";
import { storage } from "../firebase";
import { ref, getDownloadURL } from "firebase/storage";
import Header from "./Header";
import Footer from "./Footer";
import {
  FormData,
  FormErrors,
  validateField,
  validateForm,
} from "../utils/validate";
import { HeroImageWithSkeleton } from "./ImageSkeleton";
import LanguageContext from "./contexts/languageContext";

const translations = {
  en: {
    title: "Contacts",
    name: "Name",
    phone: "Phone",
    email: "Email",
    contents: "Contents",
    send: "Send",
  },
  ko: {
    title: "연락하기",
    name: "이름",
    phone: "전화번호",
    email: "이메일",
    contents: "내용",
    send: "보내기",
  },
};

const ContactsPage = () => {
  const [heroImageUrl, setHeroImageUrl] = useState("");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState({
    name: false,
    phone: false,
    email: false,
    message: false,
  });

  const [isFormValid, setIsFormValid] = useState(false);

  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("ContactsPage must be used within a LanguageProvider");
  }
  const { language } = context;
  const content = translations[language];

  useEffect(() => {
    const loadImages = async () => {
      try {
        const heroRef = ref(storage, "exhibition/exhibition_photo.webp");
        const heroUrl = await getDownloadURL(heroRef);
        setHeroImageUrl(heroUrl);
      } catch (error) {
        console.error("Error loading images:", error);
      }
    };

    loadImages();
  }, []);

  useEffect(() => {
    const hasAllRequiredFields = Object.values(formData).every(
      (value) => value.trim() !== ""
    );
    const hasNoErrors = Object.keys(errors).length === 0;
    const allFieldsTouched = Object.values(touched).every(
      (value) => value === true
    );

    console.log("Form Validation Status:", {
      hasAllRequiredFields,
      hasNoErrors,
      allFieldsTouched,
      errors,
      touched,
    });

    setIsFormValid(hasAllRequiredFields && hasNoErrors && allFieldsTouched);
  }, [formData, errors, touched]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof FormData;

    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    const error = validateField(fieldName, value, language);
    setErrors((prev) => {
      if (error) {
        return { ...prev, [fieldName]: error };
      } else {
        const { [fieldName]: _, ...rest } = prev;
        return rest;
      }
    });
  };

  const handleBlur = (name: keyof FormData) => {
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, formData[name], language);
    setErrors((prev) => {
      if (error) {
        return { ...prev, [name]: error };
      } else {
        const { [name]: _, ...rest } = prev;
        return rest;
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 모든 필드 검증
    const newErrors = validateForm(formData, language);
    setErrors(newErrors);
    setTouched({
      name: true,
      phone: true,
      email: true,
      message: true,
    });

    // 에러가 없을 경우에만 제출
    if (Object.keys(newErrors).length === 0) {
      const mailtoLink = `mailto:benchang17@naver.com?subject=Contact from ${formData.name}&body=Name: ${formData.name}%0D%0APhone: ${formData.phone}%0D%0AEmail: ${formData.email}%0D%0A%0D%0AMessage:%0D%0A${formData.message}`;
      window.location.href = mailtoLink;
    }
  };

  const getInputClassName = (fieldName: keyof FormData) => {
    const baseClasses =
      "w-full px-4 py-2 border focus:outline-none focus:ring-2";
    const hasError = touched[fieldName] && errors[fieldName];
    return `${baseClasses} ${
      hasError
        ? "border-red-500 focus:ring-red-500"
        : "border-gray-300 focus:ring-blue-500"
    }`;
  };

  return (
    <div className="relative min-h-screen bg-white">
      <Header />
      <main className="pt-20">
        <div className="flex flex-col md:flex-row min-h-screen">
          <div className="w-full md:w-2/5 p-6 pr-6 order-2 md:order-1">
            <div className="w-full">
              <h1
                className="text-6xl md:text-8xl font-bold mb-8 text-gray-800"
                style={{
                  fontFamily: '"neue-haas-grotesk-display", sans-serif',
                  fontWeight: 800,
                  fontStyle: "normal",
                }}
              >
                Contacts
              </h1>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {content.name}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur("name")}
                    className={getInputClassName("name")}
                  />
                  {touched.name && errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {content.phone}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur("phone")}
                    className={getInputClassName("phone")}
                  />
                  {touched.phone && errors.phone && (
                    <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {content.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur("email")}
                    className={getInputClassName("email")}
                  />
                  {touched.email && errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {content.contents}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={9}
                    value={formData.message}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur("message")}
                    className={getInputClassName("message")}
                  />
                  {touched.message && errors.message && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-black text-white py-3 px-6 hover:bg-gray-800 transition-colors disabled:bg-gray-400"
                  disabled={!isFormValid}
                >
                  {content.send}
                </button>
              </form>
            </div>
          </div>

          <div className="w-full md:w-3/5 order-1 md:order-2">
            <div className="sticky top-20">
              <HeroImageWithSkeleton
                imageUrl={heroImageUrl}
                alt="2024-46"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </main>

      <footer className="relative w-full">
        <Footer />
      </footer>
    </div>
  );
};

export default ContactsPage;
